/**
 * Created by Alex Xia on 04/23/2023
 * Description:
 * Calendar Service
 * ------ maintenance history ------
 */
import { Injectable } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Subject } from 'rxjs';
import { ResearchView } from '../../constants/business.constants';
import { DEFAULT_PAGE_SIZE } from '../../widgets/grid/grid.model';
import { CalendarScreenAddSideNoteComponent } from './calendar-screen-add-sidenote.component';
import { PriorityStarAggridComponent } from '../../widgets/priority-star-aggrid/priority-star-aggrid.component';
import { DateHelperService } from '../../tamalelibs/services/date-helper.service';

@Injectable({
    providedIn: 'root'
})
export class CalendarScreenService {
    constructor(
        private _dateHelperService: DateHelperService
    ) {
    }
    feedbackSubject$?: Subject<any> = new Subject<any>();

    columnDefs: Array<ColDef> =
        [
            {
                headerName: 'Sidenotes',
                headerComponentParams: {
                    template:
                        '<image src="./assets/images/singlesvg/sidenote.svg" style="width:16px;height:16px;"></image>'
                },
                valueGetter: (params) => {
                    if (params.data) {
                        return params.data.notesInThread - 1 === 0 ? '' : params.data.notesInThread - 1;
                    }
                },
                headerClass: 'icon-header-style',
                colId: 'sidenotes',
                menuTabs: [],
                width: 32,
                minWidth: 32,
                maxWidth: 32,
                sortable: false,
                resizable: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    } else {
                        return { 'text-align': 'center', 'padding-left': '8px', 'padding-right': '8px', 'text-overflow': 'unset' };
                    }
                },
            },
            {
                headerName: 'Date',
                valueGetter: (params) => {
                    return params.data ? params.data.StartTime : '';
                },
                cellRenderer: (data) => {
                    if (data.data) {
                        const startTime = data.data.StartTime;
                        if (startTime) {
                            return this._dateHelperService.getSpecificFormatOfDate(startTime, false);
                        }
                    }
                },
                tooltipValueGetter: this._tooltip,
                enableRowGroup: false,
                colId: 'displaydate',
                resizable: false,
                maxWidth: 100,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Event type',
                valueGetter: 'data.EntryType.name',
                tooltipValueGetter: this._tooltip,
                colId: 'entrytype',
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Entities',
                valueGetter: 'data.Entities',
                tooltipValueGetter: this._tooltip,
                colId: 'entity',
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Subject',
                valueGetter: 'data.Subject',
                tooltipValueGetter: this._tooltip,
                colId: 'title',
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Participants',
                valueGetter: 'data.Attendees',
                tooltipValueGetter: this._tooltip,
                colId: 'attendees',
                sortable: false,
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Time',
                valueGetter: (params) => {
                    const startTime = params.data.StartTime;
                    const endTime = params.data.EndTime;
                    if (startTime && endTime) {
                        return startTime.getHours().toString().padStart(2, '0') + ':' + startTime.getMinutes().toString().padStart(2, '0') + ' - ' + endTime.getHours().toString().padStart(2, '0') + ':' + endTime.getMinutes().toString().padStart(2, '0');
                    }
                },
                tooltipValueGetter: this._tooltip,
                colId: 'time',
                sortable: false,
                maxWidth: 110,
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Location',
                valueGetter: (params) => {
                    return params.data.Location || '';
                },
                tooltipValueGetter: this._tooltip,
                colId: 'location',
                sortable: false,
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Source',
                valueGetter: (params) => {
                    if (params.data && params.data.notes && params.data.notes.length > 0 && params.data.notes[0].source) {
                        return params.data.notes[0].source.name;
                    }
                },
                tooltipValueGetter: this._tooltip,
                colId: 'source',
                hide: true,
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Submitter',
                valueGetter: (params) => {
                    if (params.data && params.data.notes && params.data.notes.length > 0 && params.data.notes[0].submitter) {
                        return params.data.notes[0].submitter.name;
                    }
                },
                tooltipValueGetter: this._tooltip,
                colId: 'submitter',
                hide: true,
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45,
                        };
                    }
                },
            },
            {
                headerName: 'Sentiment',
                headerComponentParams: {
                    template:
                        '<image src="./assets/images/singlesvg/positive_negative.svg" style="width:16px;height:16px;"></image>'
                },
                valueGetter: (params) => {
                    if (params.data && params.data.notes && params.data.notes.length > 0) {
                        return params.data.notes[0].sentiment;
                    }
                },
                hide: true,
                sortable: false,
                headerClass: 'icon-header-style',
                cellRenderer: function (params) {
                    const imageElement = document.createElement('img');
                    imageElement.width = 13;
                    imageElement.height = 13;
                    if (params && params.value) {
                        if (params.value === 1) {
                            imageElement.src = './assets/images/singlesvg/positive-header.svg';
                            return imageElement;
                        } else if (params.value === -1) {
                            imageElement.src = './assets/images/singlesvg/negative-header.svg';
                            return imageElement;
                        } else {
                            return '';
                        }
                    }
                },
                colId: 'sentiment',
                menuTabs: [],
                width: 32,
                minWidth: 32,
                maxWidth: 32,
                cellStyle: {
                    'text-align': 'center',
                    'padding-left': '8px',
                    'padding-right': '8px',
                    'text-overflow': 'unset'
                }
            },
            {
                headerName: 'Priority',
                valueGetter: (params) => {
                    if (params.data && params.data.notes && params.data.notes.length > 0) {
                        return params.data.notes[0].priority;
                    }
                },
                cellRenderer: PriorityStarAggridComponent,
                tooltipValueGetter: this._tooltip,
                colId: 'priority',
                sortable: false,
                hide: true,
                enableRowGroup: false,
                cellStyle: function (params) {
                    if (params.data && params.data.StartTime < Date.now()) {
                        return {
                            opacity: 0.45, 'text-align': 'center', 'padding-left': '8px', 'padding-right': '8px'
                        };
                    }
                },
            },
            {
                headerName: '',
                minWidth: 32,
                width: 32,
                maxWidth: 32,
                sortable: false,
                suppressColumnsToolPanel: true,
                menuTabs: [],
                cellRenderer: CalendarScreenAddSideNoteComponent,
                cellRendererParams(params) {
                    return params.data;
                },
            },
        ];

    gridOptions: GridOptions = {
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 150,
            maxWidth: 350,
        },
        cacheBlockSize: ResearchView.cacheBlockSize,
        suppressCellFocus: true,
        suppressColumnVirtualisation: true,
        enableBrowserTooltips: true,
        paginationPageSize: DEFAULT_PAGE_SIZE,
        colResizeDefault: 'shift',
        rowSelection: 'single', // we only need to support single selection on event list.
        sortingOrder: ['desc', 'asc'],
        maintainColumnOrder: true,
        suppressContextMenu: true,
        suppressDragLeaveHidesColumns: true,
        suppressMakeColumnVisibleAfterUnGroup: true,
        suppressRowTransform: true,
        overlayNoRowsTemplate: '<span style=\'font-size: 20px;background-color:white;color:black\'>No Data Found</span>',
    };

    private _tooltip(params) {
        return (params.value);
    }
}
